import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, X, Loader2, Clock } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useDebounce } from '@/hooks/use-debounce'
import { useRecentSearches } from '@/hooks/use-recent-searches'
import { apiClient } from '@/lib/api'

interface SearchSuggestion {
  id: string
  name: string
  type: 'account' | 'contact' | 'lead' | 'opportunity'
}

export function GlobalSearchBar() {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const debouncedQuery = useDebounce(query, 300)
  const { recentSearches, addSearch, clearSearches } = useRecentSearches('global')

  // With a typed query (>=2 chars) we show live suggestions; otherwise, while focused,
  // we surface recent searches so the user can quickly re-run a previous search.
  const showSuggestions = isOpen && query.trim().length >= 2 && suggestions.length > 0
  const showRecent = isOpen && query.trim().length < 2 && recentSearches.length > 0

  // Run a free-text search: remember it and go to the full results page.
  const commitSearch = (term: string) => {
    const q = term.trim()
    if (!q) return
    addSearch(q)
    navigate(`/search?q=${encodeURIComponent(q)}`)
    setIsOpen(false)
    setSelectedIndex(-1)
    inputRef.current?.blur()
  }

  // Fetch suggestions when query changes
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (debouncedQuery.length < 2) {
        // Keep the dropdown open (it may be showing recent searches); just drop
        // any stale suggestions.
        setSuggestions([])
        return
      }

      setIsLoading(true)
      try {
        const response = (await apiClient.getSearchSuggestions(debouncedQuery, 8)) as {
          data: SearchSuggestion[]
        }
        setSuggestions(response.data || [])
        setIsOpen(true)
      } catch (error) {
        console.error('Error fetching suggestions:', error)
        setSuggestions([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchSuggestions()
  }, [debouncedQuery])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Recent-search mode (empty / short query): navigate the remembered terms.
    if (showRecent) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((prev) => (prev < recentSearches.length - 1 ? prev + 1 : 0))
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : recentSearches.length - 1))
          break
        case 'Enter': {
          e.preventDefault()
          const selectedTerm = recentSearches[selectedIndex]
          if (selectedIndex >= 0 && selectedTerm) {
            commitSearch(selectedTerm)
          } else if (query.trim()) {
            commitSearch(query)
          }
          break
        }
        case 'Escape':
          setIsOpen(false)
          setSelectedIndex(-1)
          break
      }
      return
    }

    // Suggestion mode.
    if (!isOpen || suggestions.length === 0) {
      if (e.key === 'Enter' && query.trim()) {
        commitSearch(query)
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1))
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0) {
          const selectedSuggestion = suggestions[selectedIndex]
          if (selectedSuggestion) {
            handleSelectSuggestion(selectedSuggestion)
          }
        } else if (query.trim()) {
          commitSearch(query)
        }
        break
      case 'Escape':
        setIsOpen(false)
        setSelectedIndex(-1)
        break
    }
  }

  const handleSelectSuggestion = (suggestion: SearchSuggestion) => {
    // Remember what the user typed so it shows up under recent searches.
    addSearch(query)
    // Navigate directly to the record
    const routes: Record<string, string> = {
      account: '/accounts',
      contact: '/contacts',
      lead: '/leads',
      opportunity: '/opportunities',
    }
    const basePath = routes[suggestion.type] || '/search'
    if (suggestion.id) {
      navigate(`${basePath}/${suggestion.id}`)
    } else {
      navigate(`/search?q=${encodeURIComponent(suggestion.name)}`)
    }
    setQuery('')
    setIsOpen(false)
    setSelectedIndex(-1)
    inputRef.current?.blur()
  }

  const handleClear = () => {
    setQuery('')
    setSuggestions([])
    setIsOpen(false)
    setSelectedIndex(-1)
    inputRef.current?.focus()
  }

  const getEntityIcon = (type: string) => {
    switch (type) {
      case 'account':
        return '🏢'
      case 'contact':
        return '👤'
      case 'lead':
        return '🎯'
      case 'opportunity':
        return '💼'
      default:
        return '📄'
    }
  }

  const getEntityLabel = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1)
  }

  return (
    <div className="relative w-full">
      <div className="relative">
        <Search
          className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          ref={inputRef}
          type="search"
          placeholder="Search..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            setIsOpen(true)
            setSelectedIndex(-1)
          }}
          className="pl-8 sm:pl-9 pr-8 sm:pr-9 h-8 sm:h-10 text-sm sm:text-base"
          aria-label="Search accounts, contacts, opportunities, and more"
          aria-autocomplete="list"
          aria-controls={showRecent ? 'search-recent' : 'search-suggestions'}
          aria-expanded={showRecent || showSuggestions}
          aria-activedescendant={
            selectedIndex >= 0
              ? showRecent
                ? `recent-${selectedIndex}`
                : `suggestion-${selectedIndex}`
              : undefined
          }
        />
        {query && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-0 top-1/2 -translate-y-1/2 h-8 w-8"
            onClick={handleClear}
            aria-label="Clear search"
          >
            {isLoading ? (
              <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 animate-spin" />
            ) : (
              <X className="h-3 w-3 sm:h-4 sm:w-4" />
            )}
          </Button>
        )}
      </div>

      {/* Recent searches dropdown (shown while focused with no active query) */}
      {showRecent && (
        <div
          ref={dropdownRef}
          id="search-recent"
          role="listbox"
          aria-label="Recent searches"
          className="absolute top-full mt-2 w-full bg-popover text-popover-foreground rounded-md border shadow-lg z-50 max-h-80 overflow-y-auto"
        >
          <div className="flex items-center justify-between border-b px-3 py-2">
            <span className="text-xs font-medium text-muted-foreground">Recent Searches</span>
            <button
              type="button"
              onClick={clearSearches}
              className="rounded-sm px-1 text-xs text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Clear recent searches"
            >
              Clear
            </button>
          </div>
          <div className="p-2 space-y-1">
            {recentSearches.map((term, index) => (
              <button
                key={term}
                id={`recent-${index}`}
                role="option"
                aria-selected={selectedIndex === index}
                onClick={() => commitSearch(term)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors',
                  'hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  selectedIndex === index && 'bg-accent text-accent-foreground ring-2 ring-ring'
                )}
              >
                <Clock className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
                <span className="flex-1 min-w-0 truncate">{term}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Suggestions dropdown */}
      {showSuggestions && (
        <div
          ref={dropdownRef}
          id="search-suggestions"
          role="listbox"
          className="absolute top-full mt-2 w-full bg-popover text-popover-foreground rounded-md border shadow-lg z-50 max-h-80 overflow-y-auto"
        >
          <div className="border-b p-2 text-xs text-muted-foreground text-center">
            Press Enter to view all results
          </div>
          <div className="p-2 space-y-1">
            {suggestions.map((suggestion, index) => (
              <button
                key={`${suggestion.type}-${suggestion.name}-${index}`}
                id={`suggestion-${index}`}
                role="option"
                aria-selected={selectedIndex === index}
                onClick={() => handleSelectSuggestion(suggestion)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 text-left rounded-md transition-colors',
                  'hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  selectedIndex === index && 'bg-accent text-accent-foreground ring-2 ring-ring'
                )}
              >
                <span className="text-lg" aria-hidden="true">
                  {getEntityIcon(suggestion.type)}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{suggestion.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {getEntityLabel(suggestion.type)}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* No results message */}
      {isOpen && !isLoading && query.length >= 2 && suggestions.length === 0 && (
        <div
          ref={dropdownRef}
          className="absolute top-full mt-2 w-full bg-popover text-popover-foreground rounded-md border shadow-lg z-50 p-4 text-center text-sm text-muted-foreground"
        >
          No results found for "{query}"
          <div className="mt-2 text-xs">Press Enter to search across all entities</div>
        </div>
      )}
    </div>
  )
}
